import { useMemo } from "react";
import { Icon, Text } from "@ttc3k/trekker";
import { ShoppingBag } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Product } from "gql/generated";
import { SmartImage } from "shared/components";
import { formatPrice, getMultilingualString } from "shared/utils";
import { Box, Center, Flex, styled } from "styled-system/jsx";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import {
  getCartVariantQuantities,
  MerchVariantOptions,
} from "../Steps/components/MerchVariantSelection";
import { getCatalogProductSummary } from "./merchCatalogUtils";
import { StockBadge } from "./StockBadge";

const THUMB_SIZE = 72;

/** Stacked layout for small merch catalogs: every product's options are visible inline. */
export const MerchCatalogList = () => {
  const { products } = useBookerContext();
  const { cart } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });

  const cartVariantQuantities = useMemo(
    () => getCartVariantQuantities(cart),
    [cart],
  );

  if (products.length === 0) {
    return <Text color="text.mid">{t("EMPTY")}</Text>;
  }

  return (
    <Flex flexDirection="column" gap="600" w="full">
      {products.map(product => (
        <MerchListSection
          key={product._id}
          product={product}
          cartVariantQuantities={cartVariantQuantities}
        />
      ))}
    </Flex>
  );
};

const MerchListSection = ({
  product,
  cartVariantQuantities,
}: {
  product: Product;
  cartVariantQuantities: Record<string, number>;
}) => {
  const { language } = useBookerContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_CATALOG" });
  const {
    lowestPrice,
    hasPriceRange,
    quantityInCart,
    image,
    remaining,
    stockSignal,
  } = getCatalogProductSummary(product, cartVariantQuantities);

  const name = getMultilingualString(product.name, language);
  const description = getMultilingualString(
    product.description ?? {},
    language,
  );
  const priceLabel = hasPriceRange
    ? t("FROM", { price: `$${formatPrice(lowestPrice)}` })
    : `$${formatPrice(lowestPrice)}`;

  return (
    <styled.section
      display="flex"
      flexDirection="column"
      gap="300"
      aria-label={name}
    >
      <Flex gap="300" alignItems="flex-start">
        <Box
          flexShrink={0}
          w={`${THUMB_SIZE}px`}
          h={`${THUMB_SIZE}px`}
          borderRadius="150"
          overflow="hidden"
          bg="bg.wa.lightest"
          borderWidth="1px"
          borderColor="border.light"
        >
          {image ? (
            <SmartImage
              image={image}
              width={THUMB_SIZE}
              height={THUMB_SIZE}
              shape="square"
              alt={name}
            />
          ) : (
            <Center w="full" h="full" aria-label={t("NO_IMAGE")}>
              <Icon Element={ShoppingBag} color="icon.light" />
            </Center>
          )}
        </Box>
        <Flex flexDirection="column" gap="50" minW="0" flex="1">
          <Flex gap="150" alignItems="center" flexWrap="wrap">
            <Text visual="bodySemiBold" color="text.dark">
              {name}
            </Text>
            <StockBadge
              signal={stockSignal}
              remaining={remaining}
              quantityInCart={quantityInCart}
            />
          </Flex>
          <Text visual="smallMedium" color="text.mid">
            {priceLabel}
          </Text>
          {description && (
            <Text visual="smallRegular" color="text.mid" lineClamp={2}>
              {description}
            </Text>
          )}
        </Flex>
      </Flex>
      <MerchVariantOptions product={product} />
    </styled.section>
  );
};
